import { Button, ColorPicker, Flex, Form, InputNumber, Space, Typography } from 'antd'
import { useContext, useEffect } from 'react'
import { CustomizeDiagramConfigContext, DiagramConfigContext } from "../../../../context/DiagramConfigContext.tsx"
import { DiagramConfig } from "../../../../types/diagram.ts"
import { EntityType } from "../../../../backend/types/entity.ts"

const { Title } = Typography

export const SettingsEditor = ({ onSubmit }: { onSubmit: () => void }) => {
    const { config: diagramConfig } = useContext(DiagramConfigContext)
    const { setConfig: setDiagramConfig, resetConfig: resetDiagramConfig } = useContext(CustomizeDiagramConfigContext)
    const [form] = Form.useForm()

    const save = (newConfig: DiagramConfig) => {
        setDiagramConfig(() => newConfig)
        onSubmit()
    }

    const reset = () => {
        resetDiagramConfig()
        onSubmit()
    }

    useEffect(() => {
        form.resetFields()
    }, [form, diagramConfig])

    return (
        <>
            <Title level={5} style={{ margin: '0 0 20px 0' }}>Diagram Settings</Title>
            <Form
                form={form}
                initialValues={diagramConfig}
                onFinish={save}
                labelCol={{ span: 8 }}
                labelAlign="left"
                labelWrap
                colon={false}
                wrapperCol={{ flex: 1 }}
                size={'small'}
                requiredMark='optional'
            >
                <Flex
                    vertical
                    style={{ maxHeight: '400px', overflow: 'auto', marginBottom: '20px' }}
                >
                    <Form.Item<DiagramConfig>
                        label='Nodes margin X'
                        name={['nodesMargin', 'x']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Nodes margin Y'
                        name={['nodesMargin', 'y']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Nodes width'
                        name={'nodeWidth'}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Minimal link width'
                        name={'minLinkWidth'}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Maximum link width'
                        name={'maxLinkWidth'}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Default entity color'
                        name={['defaultEntity', 'color']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Default entity label font size'
                        name={['defaultEntity', 'fontSize']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Default entity label font weight'
                        name={['defaultEntity', 'fontWeight']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Default entity font color'
                        name={['defaultEntity', 'fontColor']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Selected color'
                        name={['selected', 'color']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Selected stroke color'
                        name={['selected', 'strokeColor']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    {Object.entries(diagramConfig.entities).map(([entityType]) => (
                        <Flex
                            vertical
                            gap={10}
                            key={entityType}
                        >
                            <Form.Item<DiagramConfig>
                                label={`${entityType} color`}
                                name={['entities', entityType as EntityType, 'color']}
                                rules={[{ required: true }]}
                                normalize={(e) => e.toHexString()}
                            >
                                <ColorPicker />
                            </Form.Item>
                            <Form.Item<DiagramConfig>
                                label={`${entityType} label font size`}
                                name={['entities', entityType as EntityType, 'fontSize']}
                                rules={[{ required: true }]}
                            >
                                <InputNumber/>
                            </Form.Item>
                            <Form.Item<DiagramConfig>
                                label={`${entityType} label font weight`}
                                name={['entities', entityType as EntityType, 'fontWeight']}
                                rules={[{ required: true }]}
                            >
                                <InputNumber/>
                            </Form.Item>
                            <Form.Item<DiagramConfig>
                                label={`${entityType} label font color`}
                                name={['entities', entityType as EntityType, 'fontColor']}
                                rules={[{ required: true }]}
                                normalize={(e) => e.toHexString()}
                            >
                                <ColorPicker />
                            </Form.Item>
                        </Flex>
                    ))}
                    <Form.Item<DiagramConfig>
                        label='Link label font color'
                        name={['linkLabelText', 'fontColor']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link label stroke color'
                        name={['linkLabelText', 'strokeColor']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link label font weight'
                        name={['linkLabelText', 'fontWeight']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link label font size'
                        name={['linkLabelText', 'fontSize']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link label minimum value color'
                        name={['linkLabelText', 'fromColor']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link label maximum value color'
                        name={['linkLabelText', 'toColor']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link label minimum width'
                        name={['linkLabelText', 'minWidth']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link label maximum width'
                        name={['linkLabelText', 'maxWidth']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link amount font color'
                        name={['linkAmountText', 'fontColor']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link amount stroke color'
                        name={['linkAmountText', 'strokeColor']}
                        rules={[{ required: true }]}
                        normalize={(e) => e.toHexString()}
                    >
                        <ColorPicker />
                    </Form.Item>
                    <Form.Item<DiagramConfig>
                        label='Link amount font weight'
                        name={['linkAmountText', 'fontWeight']}
                        rules={[{ required: true }]}
                    >
                        <InputNumber/>
                    </Form.Item>
                </Flex>
                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit">
                        Save
                        </Button>
                        <Button htmlType="button" onClick={reset}>
                        Reset
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </>)
}
